import { Suspense, lazy, useEffect } from 'react';
import { api, ApiError } from './lib/api.js';
import { setSelf, useGame } from './state/store.js';
import { Spinner } from './ui/components.js';
import { LoginScreen } from './ui/LoginScreen.js';

/**
 * The in-game bundle (PixiJS, the engine and the HUD) is by far the largest
 * part of the app, so it loads on demand: the landing page renders without it,
 * then prefetches it while the player reads, so pressing Play feels instant.
 */
const loadGame = () => import('./game-entry.js');
const Clubhouse = lazy(async () => {
  const module = await loadGame();
  module.startFeatures();
  return { default: module.Clubhouse };
});

function prefetchGame(): void {
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  const start = () => void loadGame().catch(() => undefined);
  if (idle) idle(start);
  else window.setTimeout(start, 1500);
}

export function App() {
  const session = useGame((s) => s.session);

  // Resume an existing session from the cookie, if there is one.
  useEffect(() => {
    api
      .me()
      .then(({ user }) => setSelf(user))
      .catch((error: unknown) => {
        if (!(error instanceof ApiError) || error.status !== 401) console.warn('Session check failed', error);
        setSelf(null);
      });
  }, []);

  useEffect(() => {
    if (session === 'anonymous') prefetchGame();
  }, [session]);

  // The socket lives exactly as long as the signed-in session.
  useEffect(() => {
    if (session !== 'authenticated') return;
    let cancelled = false;
    let disconnect: (() => void) | null = null;
    void loadGame().then(({ engine }) => {
      if (cancelled) return;
      engine.connect();
      disconnect = () => engine.disconnect('logout');
    });
    return () => {
      cancelled = true;
      disconnect?.();
    };
  }, [session]);

  if (session === 'loading') return <Boot label="Warming up the kopi" />;
  if (session === 'anonymous') return <LoginScreen />;
  return (
    <Suspense fallback={<Boot label="Opening the kopitiam" />}>
      <Clubhouse />
    </Suspense>
  );
}

function Boot({ label }: { label: string }) {
  return (
    <main className="boot">
      <Spinner label={label} />
    </main>
  );
}
